import { InjectionToken, NgZone } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { HttpOptions, HttpService, isErrorResponse, isNil, jsonStringify } from './../../utils';
import { assertDefined, assertNotNil, isAssertOn } from './../internal/assert';
import { Sdk } from './../services/dependency/sdk/sdk';
import { le, li } from './../services/logger/logger.service';
import { ZoneService } from './../services/zone/zone.service';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'HttpServiceOnJsonp';

export const HTTP_SERVICE_ON_JSONP = new InjectionToken<HttpService>(LOGGER);

const JSONP_CALLBACK = 'callback=JSONP_CALLBACK';

// name of the SDK module
const _MODULE_NAME = Sdk.MODULE_NAME;
const _JSONP_CALLBACK_PREFIX = `${_MODULE_NAME}.jsonp.callback.`;

/*
 * Removes the script node
 *
 * @param aNode the node
 */
function _removeNode(aNode: HTMLScriptElement) {
    // delete the node
    const parent = aNode.parentNode;
    if (parent) {
        parent.removeChild(aNode);
    }
}

interface PendingRequests {
    [url: string]: Observable<any>;
}

/**
 * expect to NOT run in an Angular zone
 */
const _assertNotInAngularZone = NgZone.assertNotInAngularZone;

/**
 * expect to  run in an Angular zone
 */
const _assertInAngularZone = NgZone.assertInAngularZone;

/*
 * Sends a request to the given URL
 *
 * @param request  the request
 * @param aDoc  the document
 * @param aMap  the map of pending request, reuse if a request is pending
 * @param aSdk  the SDK hook used to manage the callback functions
 * @return an observable with the response body. In case of an error, the observable will fail. If the request
 *  is canceled, the observable will just be empty.
 */
function _sendRequest<T>(
    aUrl: string,
    aDoc: Document,
    aScope: any,
    aPending: PendingRequests): Observable<T> {
    // decode the URL
    const requestUrl = aUrl;
    // make sure we have a document
    assertNotNil(aDoc, 'Need a document to add the <script> tag.');
    // this instance
    const sdk = aScope[_MODULE_NAME] as Sdk;
    assertDefined(sdk, 'SDK needs to be defined on the global object.');

    // this instance
    const jsonp = sdk.jsonp;

    /*
     * Check if we have a pending request. If so, attach to the pending
     * request and do not send it, again.
    */
    let response: Observable<T> = aPending[requestUrl];
    if (isNil(response)) {
        // construct a new observable
        response = Observable.create((obs: Observer<T>) => {
            // expect to NOT run in an Angular zone
            _assertNotInAngularZone();
            // construct a new script element
            const node = aDoc.createElement('script');
            // check if we are done
            let bRunning = true;
            let bIncomplete = true;
            let bCanceled = false;
            // remove all listeners
            const onDone = () => {
                // do not call twice
                if (bRunning) {
                    // cleanup
                    node.removeEventListener('load', onComplete);
                    node.removeEventListener('abort', onCancel);
                    node.removeEventListener('error', onError);
                    _removeNode(node);
                    // unregister the callback
                    jsonp.removeCallback(digest);
                    // remove from the map
                    delete aPending[requestUrl];
                    // reset the flag
                    bRunning = false;
                }
            };
            // handlers
            const onData = (data: any) => {
                // expect to NOT run in an Angular zone
                _assertNotInAngularZone();
                // log this
                li(LOGGER, 'onData', requestUrl, data);
                // check if we still need to produce data
                if (bIncomplete) {
                    // check if the response is an error
                    if (isErrorResponse(data)) {
                        // bail out with the error
                        obs.error(data);
                    } else {
                        // send the event
                        obs.next(data);
                    }
                    // done
                    obs.complete();
                    bIncomplete = false;
                }
            };
            const onComplete = () => {
                // log this
                li(LOGGER, 'onComplete', requestUrl);
                // done
                onDone();
                // done with the response
                if (bIncomplete) {
                    obs.complete();
                    bIncomplete = false;
                }
            };
            const onCancel = () => {
                // log this
                li(LOGGER, 'onCancel', requestUrl);
                // set the canceled flag
                bCanceled = true;
                // dispatch
                onComplete();
            };
            const onError = err => {
                // only log if we are not canceled
                if (!bCanceled) {
                    le(LOGGER, 'onError', requestUrl, err);
                }
                // done
                onDone();
                // done with the response
                if (bIncomplete) {
                    // send the error
                    obs.error(err);
                    bIncomplete = false;
                }
            };
            /* Get the digest callback. This call registers a function in the global namespace. The
             * name of the function is derived from the URL such that the same URLs will get the same
             * callback name, but the name is a valid method identifier. This way we make sure
             * that the actual JSONP requests are cacheable, since every URL is mapped to a canonical
             * counterpart.
            */
            const digest = jsonp.addCallback(requestUrl, onData);
            const url = requestUrl.replace(/=JSONP_CALLBACK(&|$)/,
                (match, suffix) => `=${encodeURIComponent(_JSONP_CALLBACK_PREFIX + digest)}${suffix}`);
            // add the event handlers
            node.addEventListener('load', onComplete);
            node.addEventListener('abort', onCancel);
            node.addEventListener('error', onError);
            // check if we need to send credentials
            // const withCredentials = request.withCredentials;
            // execute the request
            node.type = 'text/javascript';
            node.async = true;
            node.defer = true;
            node.src = url;
            // node.crossOrigin = withCredentials ? 'use-credentials' : 'anonymous';
            aDoc.body.appendChild(node);
            // return a cancellation handler
            return onCancel;
        }).pipe(
            // cache the last value
            shareReplay(1)
        );
        /*
         * Register this. We will unregister
         * in the onDone callback method.
         */
        aPending[requestUrl] = response;
    }
    // ok
    return response;
}

/**
 * Adds the JSONP callback parameter to the URL
 *
 * @param aUrl  the URL
 * @return the callback
 */
function _addCallback(aUrl: string): string {
    // append callback
    let url = aUrl;
    if (url.indexOf('?') >= 0) {
        url += '&' + JSONP_CALLBACK;
    } else {
        url += '?' + JSONP_CALLBACK;
    }
    // ok
    return url;
}

/**
 * Implements the {@link HttpService} interface via JSONP requests.
 *
 * @param aDoc     the document
 * @param aScope   the SDK scope
 * @param aZone    the angular zone service
 * @return service implementation
 */
export function createHttpServiceOnJsonp(
    aDoc: Document,
    aScope: any,
    aZoneService: ZoneService): HttpService {

    // sanity checks
    if (isAssertOn()) {
        assertNotNil(aDoc, 'aDoc');
        assertNotNil(aScope, 'aScope');
        assertNotNil(aZoneService, 'aZoneService');
    }

    /*
     * Set of pending requests
     */
    const _pending: PendingRequests = {};

    /**
     * Implementation of the getter function
     *
     * @param aUrl  the URL
     * @param aOptions  the options
     *
     * @return the observable
     */
    function _getJson<T>(aUrl: string, aOptions: HttpOptions): Observable<T> {
        // send the request
        li(LOGGER, 'getJson', aUrl, aOptions);
        // make the request
        return _sendRequest<T>(_addCallback(aUrl), aDoc, aScope, _pending).pipe(
            // execute outside of an angular zone
            aZoneService.subscribeOutside<T>(),
            // make sure we observe inside an angular zone
            aZoneService.observeInside<T>(),
            // sanity check
            tap(_assertInAngularZone)
        );
    }

    /**
     * Implementation of the getter function
     *
     * @param aUrl  the URL
     * @param aOptions  the options
     *
     * @return the observable
     */
    function _getText(aUrl: string, aOptions: HttpOptions): Observable<string> {
        // send the request
        li(LOGGER, 'getText', aUrl, aOptions);
        // make the request
        return _getJson(aUrl, aOptions).pipe(
            map(jsonStringify)
        );
    }

    // returns the service wrapper
    return {
        'getJson': _getJson,
        'getText': _getText
    };
}
